import { useState, useEffect } from "react";
import moment from "moment";
import { BarChart3, SlidersHorizontal } from "lucide-react";
import { DispositionBreakdown } from "@/components/DispositionBreakdown";
import axios from "axios";
import Cookies from "js-cookie";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:6002/api";

const axiosInstance = axios.create({ baseURL: API_BASE_URL });
axiosInstance.interceptors.request.use((config) => {
  const token = Cookies.get("crm_auth_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

interface DateRange {
  startDate: string;
  endDate: string;
}

interface DispositionOption {
  option_id: number;
  value: string;
  display_order: number;
}

interface DispositionTree {
  l1Options: DispositionOption[];
  l2ByL1: Record<string, DispositionOption[]>;
  l3ByL2: Record<string, DispositionOption[]>;
}

export default function Dashboard() {
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  });
  const [leadBucket, setLeadBucket] = useState<string | null>(null);
  const [lastAbcDateFrom, setLastAbcDateFrom] = useState<string>("");
  const [lastAbcDateTo, setLastAbcDateTo] = useState<string>("");
  const [lastCallStatus, setLastCallStatus] = useState<string>("");
  const [lastCallDurationMin, setLastCallDurationMin] = useState<string>("");
  const [dispositionTree, setDispositionTree] = useState<DispositionTree | null>(null);
  const [l1, setL1] = useState<number | null>(null);
  const [l2, setL2] = useState<number | null>(null);
  const [l3, setL3] = useState<number | null>(null);
  const [treeLoading, setTreeLoading] = useState(false);

  const brandColor = import.meta.env.VITE_BRAND_COLOR || "#153d23";

  useEffect(() => {
    // Each bucket has its own disposition tree. 'customer' (ordered, nothing
    // delivered yet) has none of its own -- those leads are worked from the order
    // screens -- so it falls back to the abandoned-cart tree for filtering only.
    const KNOWN_TREES = ["reorder", "rto", "edd", "phone"];
    const bucketType = KNOWN_TREES.includes(leadBucket ?? "") ? leadBucket : "abandoned_cart";
    setTreeLoading(true);
    setL1(null);
    setL2(null);
    setL3(null);
    axiosInstance
      .get(`/dispositions/tree/${bucketType}`)
      .then((res) => { if (res.data.success) setDispositionTree(res.data.data); })
      .catch(() => setDispositionTree(null))
      .finally(() => setTreeLoading(false));
  }, [leadBucket]);

  const handleDateChange = (field: "startDate" | "endDate", value: string) => {
    setDateRange((prev) => ({ ...prev, [field]: value }));
  };

  const handleL1Change = (val: string) => {
    setL1(val ? Number(val) : null);
    setL2(null);
    setL3(null);
  };

  const handleL2Change = (val: string) => {
    setL2(val ? Number(val) : null);
    setL3(null);
  };

  const l2Options = l1 && dispositionTree ? (dispositionTree.l2ByL1[String(l1)] || []) : [];
  const l3Options = l2 && dispositionTree ? (dispositionTree.l3ByL2[String(l2)] || []) : [];

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="px-6 py-4 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg text-white" style={{ backgroundColor: brandColor }}>
            <BarChart3 className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-base font-semibold text-gray-900">Dashboard</h1>
            <p className="text-xs text-gray-500">Select a disposition to see agent performance</p>
          </div>
        </div>
      </div>

      {/* Body: 3/9 split */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Left: Filters (3 cols) ── */}
        <aside className="w-80 flex-shrink-0 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-5 space-y-6">

            {/* Section header */}
            <div className="flex items-center gap-2 text-gray-700">
              <SlidersHorizontal className="w-4 h-4" />
              <span className="text-sm font-semibold">Filters</span>
            </div>

            {/* Date range */}
            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Date Range</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">From</label>
                  <input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => handleDateChange("startDate", e.target.value)}
                    className="w-full px-2 py-2 border border-gray-300 rounded-md text-xs focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">To</label>
                  <input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => handleDateChange("endDate", e.target.value)}
                    className="w-full px-2 py-2 border border-gray-300 rounded-md text-xs focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Lead type */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Lead Type</p>
              <select
                value={leadBucket || ""}
                onChange={(e) => setLeadBucket(e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none"
              >
                <option value="">All Types</option>
                <option value="abandoned_cart">Abandoned Cart</option>
                <option value="reorder">Reorder</option>
              </select>
            </div>

            {/* Last ABC Date */}
            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Last ABC Date</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">From</label>
                  <input
                    type="date"
                    value={lastAbcDateFrom}
                    onChange={(e) => setLastAbcDateFrom(e.target.value)}
                    className="w-full px-2 py-2 border border-gray-300 rounded-md text-xs focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">To</label>
                  <input
                    type="date"
                    value={lastAbcDateTo}
                    onChange={(e) => setLastAbcDateTo(e.target.value)}
                    className="w-full px-2 py-2 border border-gray-300 rounded-md text-xs focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Last Call Status */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Last Call Status</p>
              <select
                value={lastCallStatus}
                onChange={(e) => setLastCallStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none"
              >
                <option value="">Any</option>
                <option value="answered">Answered</option>
                <option value="missed">Missed</option>
                <option value="initiated">Initiated</option>
                <option value="no_calls">No Calls</option>
              </select>
            </div>

            {/* Min Call Duration */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Min Call Duration (sec)</p>
              <input
                type="number"
                min="0"
                value={lastCallDurationMin}
                onChange={(e) => setLastCallDurationMin(e.target.value)}
                placeholder="e.g. 60"
                className="w-full px-2 py-2 border border-gray-300 rounded-md text-xs focus:outline-none"
              />
            </div>

            {/* Disposition */}
            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Disposition</p>

              {/* L1 */}
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Lead Status L1 <span className="text-red-400">*</span>
                </label>
                <select
                  value={l1 ?? ""}
                  onChange={(e) => handleL1Change(e.target.value)}
                  disabled={treeLoading || !dispositionTree}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
                >
                  <option value="">{treeLoading ? "Loading..." : "Select Status"}</option>
                  {dispositionTree?.l1Options.map((opt) => (
                    <option key={opt.option_id} value={opt.option_id}>{opt.value}</option>
                  ))}
                </select>
              </div>

              {/* L2 */}
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Lead Status L2 <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <select
                  value={l2 ?? ""}
                  onChange={(e) => handleL2Change(e.target.value)}
                  disabled={!l1 || l2Options.length === 0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
                >
                  <option value="">
                    {!l1 ? "Select L1 first" : l2Options.length === 0 ? "No L2 options" : "All L2"}
                  </option>
                  {l2Options.map((opt) => (
                    <option key={opt.option_id} value={opt.option_id}>{opt.value}</option>
                  ))}
                </select>
              </div>

              {/* L3 */}
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Lead Status L3 <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <select
                  value={l3 ?? ""}
                  onChange={(e) => setL3(e.target.value ? Number(e.target.value) : null)}
                  disabled={!l2 || l3Options.length === 0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
                >
                  <option value="">
                    {!l2 ? "Select L2 first" : l3Options.length === 0 ? "No L3 options" : "All L3"}
                  </option>
                  {l3Options.map((opt) => (
                    <option key={opt.option_id} value={opt.option_id}>{opt.value}</option>
                  ))}
                </select>
              </div>
            </div>

          </div>
        </aside>

        {/* ── Right: Results (9 cols) ── */}
        <main className="flex-1 overflow-y-auto p-6">
          <DispositionBreakdown
            fromDate={dateRange.startDate}
            toDate={dateRange.endDate}
            leadBucket={leadBucket}
            l1={l1}
            l2={l2}
            l3={l3}
            lastAbcDateFrom={lastAbcDateFrom || null}
            lastAbcDateTo={lastAbcDateTo || null}
            lastCallStatus={lastCallStatus || null}
            lastCallDurationMin={lastCallDurationMin ? parseInt(lastCallDurationMin) : null}
          />
        </main>

      </div>
    </div>
  );
}
